import { QueueTypeS } from "@ugg/shared/utils/queue-type-helpers";
import { hasClassicCounterpart } from "@ugg/shared/utils/multi-build-helpers";
import { SPECIAL_GAME_MODES } from "@ugg/shared/utils/special-game-modes";
import {
  getChampionBuildUrl,
  getClassicChampionBuildUrl,
  getSpecialGameModeChampionBuildUrl,
} from "@ugg/shared/routes/app-routes";

// ─────────────────────────────────────────────────────────────────────
// Resolve the right champion build page for a given queue.
//
// Three URL grammars exist and the choice depends on the queue:
//   • League Classic  /lol/champions/classic/<alias>-classic/<role?>
//   • special modes   /lol/champions/<mode>/<alias>-<mode>
//   • everything else /lol/champions/<alias>/build/<role?>
//
// Classic can't be folded into SPECIAL_GAME_MODES — that map is keyed by
// QueueTypeS and assumes the `<mode>/<alias>-<mode>` grammar plus the
// standard build pipeline, neither of which Classic uses.
//
// This lives in its own module rather than app-routes.ts because
// app-routes is deliberately dependency-light and special-game-modes
// imports *from* it; putting the resolver there would create a cycle.
// ─────────────────────────────────────────────────────────────────────

interface ChampionBuildUrlParams {
  language?: string;
  role?: string;
}

export function getChampionBuildUrlForQueue(championAlias: string, queueType?: string, params?: ChampionBuildUrlParams): string {
  const { language, role } = params || {};

  // League Classic. ARAM_MAYHEM_CLASSIC (Riot's KIWI_JADE) routes here too:
  // those games use the Classic champion kits and item set, so the modern
  // build page would be actively wrong. It loses the Mayhem augment module,
  // which is the lesser of the two errors — surfacing augments on the
  // Classic page is a follow-up.
  const isClassicQueue = queueType === QueueTypeS.CLASSIC || queueType === QueueTypeS.ARAM_MAYHEM_CLASSIC;
  if (isClassicQueue && hasClassicCounterpart(championAlias)) {
    return getClassicChampionBuildUrl(championAlias, { language, role });
  }

  const { urlName } = Object.values(SPECIAL_GAME_MODES).find((mode) => mode.queueType === queueType) || {};
  if (urlName) {
    return getSpecialGameModeChampionBuildUrl(championAlias, urlName, { language });
  }

  return getChampionBuildUrl(championAlias, { language, ...(role && { role }) });
}
